import { ActionIcon, Box, Button, ColorInput, Divider, Group, MultiSelect, NumberInput, Paper, Select, Stack, Switch, Text, Textarea, TextInput } from '@mantine/core';
import { IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { WidgetType } from '../types';
import { WidgetPropsComponentProps } from '../getWidgetPropsComponent';
import { useTranslation } from 'react-i18next';
import { KpiPropsComponent } from './KpiProps';
import { ALL_CRITICAL_STOCK_COLUMNS, CRITICAL_STOCK_COLUMN_LABELS } from './CriticalStockTable';

//TODO: unificar todas las funciones que se repitan, pasar el definition de los fields para poder poner los labels en los selects
// Default/fallback component when no specific component is available
const DefaultWidgetPropsComponent = () => {
  const { t } = useTranslation( 'widgets' );
  return (
    <div>
      <Text>
        {t( 'widget_builder_props_default_message','No specific configuration available for this widget type.' )}
      </Text>
    </div>
  );
};

// Shared component for chart props
const CartesianChartPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetComponentProps = config.widgetComponentProps || {};
  const dimensions = config.dimensions || [];
  const metrics = config.metrics || [];
  // Initialize reference lines state
  const [ referenceLines, setReferenceLines ] = useState<Array<{ x?: string, y?: number, label?: string, color?: string }>>( widgetComponentProps.referenceLines || []);
  const [ newReferenceLine, setNewReferenceLine ] = useState<{ x?: string, y?: number, label?: string, color?: string }>({});
  const [ showAddReferenceLine, setShowAddReferenceLine ] = useState( false );
  const [ editingReferenceLineIndex, setEditingReferenceLineIndex ] = useState<number | null>( null );

  // Handle adding a new reference line
  const handleAddReferenceLine = () => {
    let updatedReferenceLines;
    if ( editingReferenceLineIndex !== null ) {
      updatedReferenceLines = [...referenceLines];
      updatedReferenceLines[editingReferenceLineIndex] = newReferenceLine;
    } else {
      updatedReferenceLines = [ ...referenceLines, newReferenceLine ];
    }
    setReferenceLines( updatedReferenceLines );
    handleUpdateProps( 'referenceLines', updatedReferenceLines );
    setNewReferenceLine({});
    setShowAddReferenceLine( false );
    setEditingReferenceLineIndex( null );
  };

  // Handle removing a reference line
  const handleRemoveReferenceLine = ( index: number ) => {
    const updatedReferenceLines = referenceLines.filter(( _, i ) => i !== index );
    setReferenceLines( updatedReferenceLines );
    handleUpdateProps( 'referenceLines', updatedReferenceLines );
  };
  const handleUpdateProps = ( key: string, value: any ) => {
    const updatedProps = {
      ...widgetComponentProps,
      [key]: value
    };
    updateConfig( 'widgetComponentProps', updatedProps );
  };

  const getDimensionOptions = () => dimensions.map( dim => ({ value: dim.field, label: dim.label || dim.field }));
  const getMetricOptions = () => metrics.map( metric => ({ value: metric.field, label: metric.label || `${metric.operation}(${metric.field})` }));

  return (
    <Stack gap="md">
      <Group grow align="flex-start">
        <Select
          label={t( 'widget_builder_cartesian_xaxis_label','X-Axis (Label)' )}
          description={t( 'widget_builder_cartesian_xaxis_description','Select dimension for x-axis' )}
          placeholder={t( 'widget_builder_cartesian_xaxis_placeholder','Select dimension' )}
          data={getDimensionOptions()}
          value={widgetComponentProps.xAxis || ''}
          onChange={value => handleUpdateProps( 'xAxis', value )}
          clearable={false}
        />
        <Select
          label={t( 'widget_builder_cartesian_yaxis_label','Y-Axis (Metric)' )}
          description={t( 'widget_builder_cartesian_yaxis_description','Select metric for y-axis' )}
          placeholder={t( 'widget_builder_cartesian_yaxis_placeholder','Select metric' )}
          data={getMetricOptions()}
          value={getMetricOptions().find( mo => mo.label === widgetComponentProps.yAxis )?.value || ''}
          onChange={( value, option ) => handleUpdateProps( 'yAxis', option.label )}
          clearable={false}
        />
      </Group>
      <Select
        label={t( 'widget_builder_cartesian_series_label','Series' )}
        description={t( 'widget_builder_cartesian_series_description','Select dimension for series (different from x-axis)' )}
        placeholder={t( 'widget_builder_cartesian_series_placeholder','Select dimension' )}
        data={getDimensionOptions().filter( dim => dim.value !== widgetComponentProps.xAxis )}
        value={widgetComponentProps.series || ''}
        onChange={value => handleUpdateProps( 'series', value )}
        clearable={false}
      />
      <Group grow align="flex-start">
        <Select
          label={t( 'widget_builder_cartesian_gridaxis_label','Grid Axis' )}
          description={t( 'widget_builder_cartesian_gridaxis_description','Show grid lines on axes' )}
          placeholder={t( 'widget_builder_cartesian_gridaxis_placeholder','Select option' )}
          data={[
            { value: 'x', label: t( 'widget_builder_cartesian_gridaxis_option_x','X-Axis Only' ) },
            { value: 'y', label: t( 'widget_builder_cartesian_gridaxis_option_y','Y-Axis Only' ) },
            { value: 'xy', label: t( 'widget_builder_cartesian_gridaxis_option_xy','Both Axes' ) },
            { value: 'none', label: t( 'widget_builder_cartesian_gridaxis_option_none','None' ) }
          ]}
          value={widgetComponentProps.gridAxis || 'xy'}
          onChange={value => handleUpdateProps( 'gridAxis', value )}
          clearable={false}
        />
        <Select
          label={t( 'widget_builder_cartesian_tickline_label','Tick Line' )}
          description={t( 'widget_builder_cartesian_tickline_description','Show tick lines on axes' )}
          placeholder={t( 'widget_builder_cartesian_tickline_placeholder','Select option' )}
          data={[
            { value: 'x', label: t( 'widget_builder_cartesian_tickline_option_x','X-Axis Only' ) },
            { value: 'y', label: t( 'widget_builder_cartesian_tickline_option_y','Y-Axis Only' ) },
            { value: 'xy', label: t( 'widget_builder_cartesian_tickline_option_xy','Both Axes' ) },
            { value: 'none', label: t( 'widget_builder_cartesian_tickline_option_none','None' ) }
          ]}
          value={widgetComponentProps.tickLine || 'xy'}
          onChange={value => handleUpdateProps( 'tickLine', value )}
          clearable={false}
        />
      </Group>
      <Select
        label={t( 'widget_builder_cartesian_curve_label','Curve Type' )}
        description={t( 'widget_builder_cartesian_curve_description','Select curve interpolation type' )}
        placeholder={t( 'widget_builder_cartesian_curve_placeholder','Select curve type' )}
        data={[
          { value: 'linear', label: t( 'widget_builder_cartesian_curve_option_linear','Linear' ) },
          { value: 'bump', label: t( 'widget_builder_cartesian_curve_option_bump','Bump' ) },
          { value: 'natural', label: t( 'widget_builder_cartesian_curve_option_natural','Natural' ) },
          { value: 'monotone', label: t( 'widget_builder_cartesian_curve_option_monotone','Monotone' ) },
          { value: 'step', label: t( 'widget_builder_cartesian_curve_option_step','Step' ) },
          { value: 'stepBefore', label: t( 'widget_builder_cartesian_curve_option_stepBefore','Step Before' ) },
          { value: 'stepAfter', label: t( 'widget_builder_cartesian_curve_option_stepAfter','Step After' ) }
        ]}
        value={widgetComponentProps.curveType || 'linear'}
        onChange={value => handleUpdateProps( 'curveType', value )}
        clearable={false}
      />
      <Divider label={t( 'widget_builder_props_display_options','Display Options' )} labelPosition="center" />
      <Group grow>
        <Switch
          label={t( 'widget_builder_props_show_dots','Show Dots' )}
          checked={widgetComponentProps.withDots !== false}
          onChange={event => handleUpdateProps( 'withDots', event.currentTarget.checked )}
        />
        <Switch
          label={t( 'widget_builder_props_show_xaxis','Show X-Axis' )}
          checked={widgetComponentProps.withXAxis !== false}
          onChange={event => handleUpdateProps( 'withXAxis', event.currentTarget.checked )}
        />
      </Group>
      <Group grow>
        <Switch
          label={t( 'widget_builder_props_show_yaxis','Show Y-Axis' )}
          checked={widgetComponentProps.withYAxis !== false}
          onChange={event => handleUpdateProps( 'withYAxis', event.currentTarget.checked )}
        />
        <Switch
          label={t( 'widget_builder_props_use_gradient','Use Gradient' )}
          checked={widgetComponentProps.withGradient === true}
          onChange={event => handleUpdateProps( 'withGradient', event.currentTarget.checked )}
        />
      </Group>
      <Group grow>
        <Switch
          label={t( 'widget_builder_props_connect_nulls','Connect Null Values' )}
          checked={widgetComponentProps.connectNulls === true}
          onChange={event => handleUpdateProps( 'connectNulls', event.currentTarget.checked )}
        />
        <Switch
          label={t( 'widget_builder_props_show_point_labels','Show Point Labels' )}
          checked={widgetComponentProps.withPointLabels === true}
          onChange={event => handleUpdateProps( 'withPointLabels', event.currentTarget.checked )}
        />
      </Group>
      <Divider label={t( 'widget_builder_props_reference_lines','Reference Lines' )} labelPosition="center" />
      
      {/* List of reference lines */}
      {referenceLines.length > 0 && (
        <Stack gap="xs">
          {referenceLines.map(( line, index ) => (
            <Paper key={index} p="xs" withBorder>
              <Group justify="space-between">
                <Group>
                  {line.color && (
                    <Box
                      style={{
                        width: 16,
                        height: 16,
                        backgroundColor: line.color,
                        borderRadius: 4,
                        border: '1px solid var(--mantine-color-gray-3)'
                      }}
                    />
                  )}
                  <Text size="sm">
                    {line.y !== undefined && `Y: ${line.y}`}
                    {line.x !== undefined && `X: ${line.x}`}
                    {line.label && ` - ${line.label}`}
                  </Text>
                </Group>
                <Group gap="xs">
                  <ActionIcon color="blue.6" variant='subtle' onClick={() => {
                    setNewReferenceLine({ ...line });
                    setEditingReferenceLineIndex( index );
                    setShowAddReferenceLine( true );
                  }}>
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon color="danger.6" variant='subtle' onClick={() => handleRemoveReferenceLine( index )}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Add reference line form */}
     {showAddReferenceLine ? (
        <Paper p="sm" withBorder>
          <Stack gap="xs">
            <Group grow>
              <TextInput
                label={t( 'widget_builder_props_xvalue_label','X Value' )}
                placeholder={t( 'widget_builder_props_xvalue_placeholder','e.g. 100' )}
                type="number"
                value={newReferenceLine.x || ''}
                onChange={e => setNewReferenceLine({ ...newReferenceLine, x: e.target.value })}
              />
              <TextInput
                label={t( 'widget_builder_props_yvalue_label','Y Value' )}
                placeholder={t( 'widget_builder_props_yvalue_placeholder','e.g. 40' )}
                type="number"
                value={newReferenceLine.y?.toString() || ''}
                onChange={e => setNewReferenceLine({ ...newReferenceLine, y: parseFloat( e.target.value ) || undefined })}
              />
            </Group>
            <Group grow>
              <TextInput
                label={t( 'widget_builder_props_label_label','Label' )}
                placeholder={t( 'widget_builder_props_label_placeholder','e.g. Average sales' )}
                value={newReferenceLine.label || ''}
                onChange={e => setNewReferenceLine({ ...newReferenceLine, label: e.target.value })}
              />
              <ColorInput
                label={t( 'widget_builder_props_color_label','Color' )}
                placeholder={t( 'widget_builder_props_color_placeholder','Pick a color' )}
                value={newReferenceLine.color || ''}
                onChange={color => setNewReferenceLine({ ...newReferenceLine, color })}
                format="hexa"
                swatches={[ '#25262b', '#868e96', '#fa5252', '#e64980', '#be4bdb', '#7950f2', '#4c6ef5', '#228be6', '#15aabf', '#12b886', '#40c057', '#82c91e', '#fab005', '#fd7e14' ]}
              />
            </Group>
            <Group justify="right" mt="xs">
              <Button variant="subtle" onClick={() => {
                setShowAddReferenceLine( false );
                setEditingReferenceLineIndex( null );
                setNewReferenceLine({});
              }}>
                {t( 'widget_builder_props_cancel_button','Cancel' )}
              </Button>
              <Button onClick={handleAddReferenceLine}>
                {editingReferenceLineIndex !== null
                  ? t( 'widget_builder_props_update_button','Update' )
                  : t( 'widget_builder_props_add_button','Add' )}
              </Button>
            </Group>
          </Stack>
        </Paper>
      ) : (
        <Button leftSection={<IconPlus size={16} />} variant="light" onClick={() => setShowAddReferenceLine( true )} fullWidth>
          {t( 'widget_builder_props_add_reference_line_button','Add Reference Line' )}
        </Button>
      )}
    </Stack>
  );
};

// Area Chart specific props component
const AreaChartPropsComponent = CartesianChartPropsComponent;

// Line Chart specific props component
const LineChartPropsComponent = CartesianChartPropsComponent;

// Bar Chart specific props component
const BarChartPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetComponentProps = config.widgetComponentProps || {};
  const dimensions = config.dimensions || [];
  const metrics = config.metrics || [];

  const [ referenceLines, setReferenceLines ] = useState<Array<{ x?: string, y?: number, label?: string, color?: string }>>( widgetComponentProps.referenceLines || []);
  const [ newReferenceLine, setNewReferenceLine ] = useState<{ x?: string, y?: number, label?: string, color?: string }>({});
  const [ showAddReferenceLine, setShowAddReferenceLine ] = useState( false );
  const [ editingReferenceLineIndex, setEditingReferenceLineIndex ] = useState<number | null>( null );

  // Handle adding a new reference line
  const handleAddReferenceLine = () => {
    let updatedReferenceLines;
    
    if ( editingReferenceLineIndex !== null ) {
      // Update existing reference line
      updatedReferenceLines = [...referenceLines];
      updatedReferenceLines[editingReferenceLineIndex] = newReferenceLine;
    } else {
      // Add new reference line
      updatedReferenceLines = [ ...referenceLines, newReferenceLine ];
    }
    
    setReferenceLines( updatedReferenceLines );
    handleUpdateProps( 'referenceLines', updatedReferenceLines );
    setNewReferenceLine({});
    setShowAddReferenceLine( false );
    setEditingReferenceLineIndex( null );
  };

  // Handle removing a reference line
  const handleRemoveReferenceLine = ( index: number ) => {
    const updatedReferenceLines = referenceLines.filter(( _, i ) => i !== index );
    setReferenceLines( updatedReferenceLines );
    handleUpdateProps( 'referenceLines', updatedReferenceLines );
  };
  const handleUpdateProps = ( key: string, value: any ) =>
    updateConfig( 'widgetComponentProps', { ...widgetComponentProps, [key]: value });

  const getDimensionOptions = () => dimensions.map( d => ({ value: d.field, label: d.label || d.field }));
  const getMetricOptions = () => metrics.map( m => ({ value: m.field, label: m.label || `${m.operation}(${m.field})` }));

  return (
    <Stack gap="md">
      <Group grow align="flex-start">
        <Select
          label={t( 'widget_builder_cartesian_xaxis_label','X-Axis (Label)' )}
          description={t( 'widget_builder_cartesian_xaxis_description','Select dimension for x-axis' )}
          placeholder={t( 'widget_builder_cartesian_xaxis_placeholder','Select dimension' )}
          data={getDimensionOptions()}
          value={widgetComponentProps.xAxis || ''}
          onChange={v => handleUpdateProps( 'xAxis', v )}
          clearable={false}
        />
        <Select
          label={t( 'widget_builder_cartesian_yaxis_label','Y-Axis (Metric)' )}
          description={t( 'widget_builder_cartesian_yaxis_description','Select metric for y-axis' )}
          placeholder={t( 'widget_builder_cartesian_yaxis_placeholder','Select metric' )}
          data={getMetricOptions()}
          value={getMetricOptions().find( mo => mo.label === widgetComponentProps.yAxis )?.value || ''}
          onChange={( v, opt ) => handleUpdateProps( 'yAxis', opt.label )}
          clearable={false}
        />
      </Group>

      <Select
        label={t( 'widget_builder_cartesian_series_label','Series' )}
        description={t( 'widget_builder_cartesian_series_description','Select dimension for series (different from x-axis)' )}
        placeholder={t( 'widget_builder_cartesian_series_placeholder','Select dimension' )}
        data={getDimensionOptions().filter( d => d.value !== widgetComponentProps.xAxis )}
        value={widgetComponentProps.series || ''}
        onChange={v => handleUpdateProps( 'series', v )}
        clearable={false}
      />

      <Group grow align="flex-start">
        <Select
          label={t( 'widget_builder_cartesian_gridaxis_label','Grid Axis' )}
          description={t( 'widget_builder_cartesian_gridaxis_description','Show grid lines on axes' )}
          placeholder={t( 'widget_builder_cartesian_gridaxis_placeholder','Select option' )}
          data={[
            { value:'x', label:t( 'widget_builder_cartesian_gridaxis_option_x','X-Axis Only' ) },
            { value:'y', label:t( 'widget_builder_cartesian_gridaxis_option_y','Y-Axis Only' ) },
            { value:'xy', label:t( 'widget_builder_cartesian_gridaxis_option_xy','Both Axes' ) },
            { value:'none', label:t( 'widget_builder_cartesian_gridaxis_option_none','None' ) }
          ]}
          value={widgetComponentProps.gridAxis || 'xy'}
          onChange={v => handleUpdateProps( 'gridAxis', v )}
          clearable={false}
        />
        <Select
          label={t( 'widget_builder_cartesian_tickline_label','Tick Line' )}
          description={t( 'widget_builder_cartesian_tickline_description','Show tick lines on axes' )}
          placeholder={t( 'widget_builder_cartesian_tickline_placeholder','Select option' )}
          data={[
            { value:'x', label:t( 'widget_builder_cartesian_tickline_option_x','X-Axis Only' ) },
            { value:'y', label:t( 'widget_builder_cartesian_tickline_option_y','Y-Axis Only' ) },
            { value:'xy', label:t( 'widget_builder_cartesian_tickline_option_xy','Both Axes' ) },
            { value:'none', label:t( 'widget_builder_cartesian_tickline_option_none','None' ) }
          ]}
          value={widgetComponentProps.tickLine || 'xy'}
          onChange={v => handleUpdateProps( 'tickLine', v )}
          clearable={false}
        />
      </Group>

      <Divider label={t( 'widget_builder_bar_options_title','Bar Chart Options' )} labelPosition="center" />

      <Group grow>
        <Select
          label={t( 'widget_builder_bar_orientation_label','Orientation' )}
          description={t( 'widget_builder_bar_orientation_description','Select bar orientation' )}
          placeholder={t( 'widget_builder_bar_orientation_placeholder','Select orientation' )}
          data={[
            { value:'vertical', label:t( 'widget_builder_bar_orientation_option_vertical','Vertical' ) },
            { value:'horizontal', label:t( 'widget_builder_bar_orientation_option_horizontal','Horizontal' ) }
          ]}
          value={widgetComponentProps.orientation || 'horizontal'}
          onChange={v => handleUpdateProps( 'orientation', v )}
          clearable={false}
        />
      </Group>

      <Group grow>
        <Switch
          label={t( 'widget_builder_props_show_xaxis','Show X-Axis' )}
          checked={widgetComponentProps.withXAxis !== false}
          onChange={e => handleUpdateProps( 'withXAxis', e.currentTarget.checked )}
        />
        <Switch
          label={t( 'widget_builder_props_show_yaxis','Show Y-Axis' )}
          checked={widgetComponentProps.withYAxis !== false}
          onChange={e => handleUpdateProps( 'withYAxis', e.currentTarget.checked )}
        />
      </Group>

      <Group grow>
        <Switch
          label={t( 'widget_builder_bar_value_labels','Show Bar Value Labels' )}
          checked={widgetComponentProps.withBarValueLabel === true}
          onChange={e => handleUpdateProps( 'withBarValueLabel', e.currentTarget.checked )}
        />
      </Group>

      <Divider label={t( 'widget_builder_props_reference_lines','Reference Lines' )} labelPosition="center" />
      
      {/* List of reference lines */}
      {referenceLines.length > 0 && (
        <Stack gap="xs">
          {referenceLines.map(( line, index ) => (
            <Paper key={index} p="xs" withBorder>
              <Group justify="space-between">
                <Group>
                  {line.color && (
                    <Box
                      style={{
                        width: 16,
                        height: 16,
                        backgroundColor: line.color,
                        borderRadius: 4,
                        border: '1px solid var(--mantine-color-gray-3)'
                      }}
                    />
                  )}
                  <Text size="sm">
                    {line.y !== undefined && `Y: ${line.y}`}
                    {line.x !== undefined && `X: ${line.x}`}
                    {line.label && ` - ${line.label}`}
                  </Text>
                </Group>
                <Group gap="xs">
                  <ActionIcon color="blue.6" variant='subtle' onClick={() => {
                    setNewReferenceLine({ ...line });
                    setEditingReferenceLineIndex( index );
                    setShowAddReferenceLine( true );
                  }}>
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon color="danger.6" variant='subtle' onClick={() => handleRemoveReferenceLine( index )}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

      {/* Add reference line form */}
        {showAddReferenceLine ? (
        <Paper p="sm" withBorder>
          <Stack gap="xs">
            <Group grow>
              <TextInput
                label={t( 'widget_builder_props_xvalue_label','X Value' )}
                placeholder={t( 'widget_builder_props_xvalue_placeholder','e.g. 100' )}
                type="number"
                value={newReferenceLine.x || ''}
                onChange={e => setNewReferenceLine({ ...newReferenceLine, x: e.target.value })}
              />
              <TextInput
                label={t( 'widget_builder_props_yvalue_label','Y Value' )}
                placeholder={t( 'widget_builder_props_yvalue_placeholder','e.g. 40' )}
                type="number"
                value={newReferenceLine.y?.toString() || ''}
                onChange={e => setNewReferenceLine({ ...newReferenceLine, y: parseFloat( e.target.value ) || undefined })}
              />
            </Group>
            <Group grow>
              <TextInput
                label={t( 'widget_builder_props_label_label','Label' )}
                placeholder={t( 'widget_builder_props_label_placeholder','e.g. Average sales' )}
                value={newReferenceLine.label || ''}
                onChange={e => setNewReferenceLine({ ...newReferenceLine, label: e.target.value })}
              />
              <ColorInput
                label={t( 'widget_builder_props_color_label','Color' )}
                placeholder={t( 'widget_builder_props_color_placeholder','Pick a color' )}
                value={newReferenceLine.color || ''}
                onChange={color => setNewReferenceLine({ ...newReferenceLine, color })}
                format="hexa"
                swatches={[ '#25262b', '#868e96', '#fa5252', '#e64980', '#be4bdb', '#7950f2', '#4c6ef5', '#228be6', '#15aabf', '#12b886', '#40c057', '#82c91e', '#fab005', '#fd7e14' ]}
              />
            </Group>
            <Group justify="right" mt="xs">
              <Button variant="subtle" onClick={() => {
                setShowAddReferenceLine( false );
                setEditingReferenceLineIndex( null );
                setNewReferenceLine({});
              }}>
                {t( 'widget_builder_props_cancel_button','Cancel' )}
              </Button>
              <Button onClick={handleAddReferenceLine}>
                {editingReferenceLineIndex !== null
                  ? t( 'widget_builder_props_update_button','Update' )
                  : t( 'widget_builder_props_add_button','Add' )}
              </Button>
            </Group>
          </Stack>
        </Paper>
      ) : (
        <Button leftSection={<IconPlus size={16} />} variant="light" onClick={() => setShowAddReferenceLine( true )} fullWidth>
          {t( 'widget_builder_props_add_reference_line_button','Add Reference Line' )}
        </Button>
      )}
    </Stack>
  );
};

const RadialChartPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetProps = config.widgetComponentProps || {};
  const dimensions = config.dimensions || [];
  const metrics = config.metrics || [];

  const handleUpdate = ( key: string, value: any ) => {
    updateConfig( 'widgetComponentProps', { ...widgetProps, [key]: value });
  };

  const dimensionOptions = dimensions.map( d => ({ value: d.field, label: d.label || d.field }));
  const metricOptions = metrics.map( m => ({ value: m.field, label: m.label || `${m.operation}(${m.field})` }));

  return (
    <Stack gap="md">
      <Select
        label={t( 'widget_builder_radial_group_by_label','Group By (Dimension)' )}
        description={t( 'widget_builder_radial_group_by_description','Select dimension to group slices' )}
        data={dimensionOptions}
        value={widgetProps.groupBy || ''}
        onChange={v => handleUpdate( 'groupBy', v )}
        clearable={false}
      />

      <Select
        label={t( 'widget_builder_radial_value_metric_label','Value Metric' )}
        description={t( 'widget_builder_radial_value_metric_description','Select metric for slice size' )}
        data={metricOptions}
        value={metricOptions.find( mo => mo.label === widgetProps.valueMetric )?.value || ''}
        onChange={( value, option ) => handleUpdate( 'valueMetric', option.label )}
        clearable={false}
      />

      <Group grow>
        <Switch
          label={t( 'widget_builder_radial_show_labels','Show Labels' )}
          checked={widgetProps.withLabels === true}
          onChange={e => handleUpdate( 'withLabels', e.currentTarget.checked )}
        />
        <Switch
          label={t( 'widget_builder_radial_show_label_lines','Show Label Lines' )}
          checked={widgetProps.withLabelsLine === true}
          onChange={e => handleUpdate( 'withLabelsLine', e.currentTarget.checked )}
        />
      </Group>

      <Group grow>
        <Switch
          label={t( 'widget_builder_radial_show_tooltips','Show Tooltips' )}
          checked={widgetProps.withTooltip === true}
          onChange={e => handleUpdate( 'withTooltip', e.currentTarget.checked )}
        />
      </Group>

      <Select
        label={t( 'widget_builder_radial_label_type_label','Label Type' )}
        description={t( 'widget_builder_radial_label_type_description','Choose label display type' )}
        data={[
          { value: 'value', label: t( 'widget_builder_radial_label_type_option_value','Value' ) },
          { value: 'percent', label: t( 'widget_builder_radial_label_type_option_percent','Percent' ) }
        ]}
        value={widgetProps.labelsType || 'value'}
        onChange={v => handleUpdate( 'labelsType', v )}
        clearable={false}
      />
    </Stack>
  );
};
// Pie Chart specific props component
const PieChartPropsComponent = RadialChartPropsComponent;

// Donut Chart specific props component
const DonutChartPropsComponent = RadialChartPropsComponent;

// Scatter Chart specific props component
const ScatterChartPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetComponentProps = config.widgetComponentProps || {};
  const dimensions = config.dimensions || [];
  const metrics = config.metrics || [];
  
  // Initialize reference lines state
  const [ referenceLines, setReferenceLines ] = useState<Array<{ x?: string, y?: number, label?: string, color?: string }>>( widgetComponentProps.referenceLines || []);
  const [ newReferenceLine, setNewReferenceLine ] = useState<{ x?: string, y?: number, label?: string, color?: string }>({});
  const [ showAddReferenceLine, setShowAddReferenceLine ] = useState( false );
  const [ editingReferenceLineIndex, setEditingReferenceLineIndex ] = useState<number | null>( null );

  // Handle adding a new reference line
  const handleAddReferenceLine = () => {
    let updatedReferenceLines;
    
    if ( editingReferenceLineIndex !== null ) {
      // Update existing reference line
      updatedReferenceLines = [...referenceLines];
      updatedReferenceLines[editingReferenceLineIndex] = newReferenceLine;
    } else {
      // Add new reference line
      updatedReferenceLines = [ ...referenceLines, newReferenceLine ];
    }
    
    setReferenceLines( updatedReferenceLines );
    handleUpdateProps( 'referenceLines', updatedReferenceLines );
    setNewReferenceLine({});
    setShowAddReferenceLine( false );
    setEditingReferenceLineIndex( null );
  };

  // Handle removing a reference line
  const handleRemoveReferenceLine = ( index: number ) => {
    const updatedReferenceLines = referenceLines.filter(( _, i ) => i !== index );
    setReferenceLines( updatedReferenceLines );
    handleUpdateProps( 'referenceLines', updatedReferenceLines );
  };

  const handleUpdateProps = ( key: string, value: any ) => {
    const updatedProps = {
      ...widgetComponentProps,
      [key]: value
    };
    updateConfig( 'widgetComponentProps', updatedProps );
  };
  const getDimensionOptions = () => {
    return dimensions?.map( dim => ({
      value: dim.field,
      label: dim.label || dim.field
    })) || [];
  };

  // Get combined options for scatter plot axes (both metrics and dimensions)
  const getAxisOptions = () => {
    const metricOptions = metrics?.map( metric => ({
      value: metric.field,
      label: metric.label || `${metric.operation}(${metric.field})`
    })) || [];
    
    const dimensionOptions = dimensions?.map( dim => ({
      value: dim.field,
      label: dim.label || dim.field
    })) || [];
    
    return [ ...metricOptions, ...dimensionOptions ];
  };

  // Get the current dataKey or initialize it
  const dataKey = widgetComponentProps.dataKey || { x: '', y: '' };

  return (
    <Stack gap="md">
      <Divider label={t( 'widget_builder_scatter_data_title','Scatter Plot Data' )} labelPosition="center" />

      <Group grow align="flex-start">
        <Select
          label={t( 'widget_builder_scatter_xaxis_label','X-Axis' )}
          description={t( 'widget_builder_scatter_xaxis_description','Select metric or dimension for x-axis' )}
          placeholder={t( 'widget_builder_scatter_xaxis_placeholder','Select field' )}
          data={getAxisOptions()}
          value={dataKey.x || ''}
          onChange={v => handleUpdateProps( 'dataKey', { ...dataKey, x: v })}
          clearable={false}
        />
        <Select
          label={t( 'widget_builder_scatter_yaxis_label','Y-Axis' )}
          description={t( 'widget_builder_scatter_yaxis_description','Select metric or dimension for y-axis' )}
          placeholder={t( 'widget_builder_scatter_yaxis_placeholder','Select field' )}
          data={getAxisOptions()}
          value={dataKey.y || ''}
          onChange={v => handleUpdateProps( 'dataKey', { ...dataKey, y: v })}
          clearable={false}
        />
      </Group>

      <Select
        label={t( 'widget_builder_scatter_group_by_label','Group By' )}
        description={t( 'widget_builder_scatter_group_by_description','Select dimension to group data points' )}
        placeholder={t( 'widget_builder_scatter_group_by_placeholder','Select dimension' )}
        data={getDimensionOptions()}
        value={widgetComponentProps.groupBy || ''}
        onChange={v => handleUpdateProps( 'groupBy', v )}
        clearable={false}
      />

      <Group grow align="flex-start">
        <Select
          label={t( 'widget_builder_scatter_gridaxis_label','Grid Axis' )}
          description={t( 'widget_builder_scatter_gridaxis_description','Show grid lines on axes' )}
          placeholder={t( 'widget_builder_scatter_gridaxis_placeholder','Select option' )}
          data={[
            { value: 'x', label: t( 'widget_builder_scatter_gridaxis_option_x','X-Axis Only' ) },
            { value: 'y', label: t( 'widget_builder_scatter_gridaxis_option_y','Y-Axis Only' ) },
            { value: 'xy', label: t( 'widget_builder_scatter_gridaxis_option_xy','Both Axes' ) },
            { value: 'none', label: t( 'widget_builder_scatter_gridaxis_option_none','None' ) }
          ]}
          value={widgetComponentProps.gridAxis || 'xy'}
          onChange={v => handleUpdateProps( 'gridAxis', v )}
          clearable={false}
        />
        <Select
          label={t( 'widget_builder_scatter_tickline_label','Tick Line' )}
          description={t( 'widget_builder_scatter_tickline_description','Show tick lines on axes' )}
          placeholder={t( 'widget_builder_scatter_tickline_placeholder','Select option' )}
          data={[
            { value: 'x', label: t( 'widget_builder_scatter_tickline_option_x','X-Axis Only' ) },
            { value: 'y', label: t( 'widget_builder_scatter_tickline_option_y','Y-Axis Only' ) },
            { value: 'xy', label: t( 'widget_builder_scatter_tickline_option_xy','Both Axes' ) },
            { value: 'none', label: t( 'widget_builder_scatter_tickline_option_none','None' ) }
          ]}
          value={widgetComponentProps.tickLine || 'xy'}
          onChange={v => handleUpdateProps( 'tickLine', v )}
          clearable={false}
        />
      </Group>

      <Divider label={t( 'widget_builder_props_display_options','Display Options' )} labelPosition="center" />

      <Group grow>
        <Switch
          label={t( 'widget_builder_props_show_xaxis','Show X-Axis' )}
          checked={widgetComponentProps.withXAxis !== false}
          onChange={e => handleUpdateProps( 'withXAxis', e.currentTarget.checked )}
        />
        <Switch
          label={t( 'widget_builder_props_show_yaxis','Show Y-Axis' )}
          checked={widgetComponentProps.withYAxis !== false}
          onChange={e => handleUpdateProps( 'withYAxis', e.currentTarget.checked )}
        />
      </Group>

      <Group grow>
        <Switch
          label={t( 'widget_builder_props_show_point_labels','Show Point Labels' )}
          checked={widgetComponentProps.withPointLabels === true}
          onChange={e => handleUpdateProps( 'withPointLabels', e.currentTarget.checked )}
        />
      </Group>
     <Divider label={t( 'widget_builder_props_reference_lines','Reference Lines' )} labelPosition="center" />
      
      {/* List of reference lines */}
      {referenceLines.length > 0 && (
        <Stack gap="xs">
          {referenceLines.map(( line, index ) => (
            <Paper key={index} p="xs" withBorder>
              <Group justify="space-between">
                <Group>
                  {line.color && (
                    <Box
                      style={{
                        width: 16,
                        height: 16,
                        backgroundColor: line.color,
                        borderRadius: 4,
                        border: '1px solid var(--mantine-color-gray-3)'
                      }}
                    />
                  )}
                  <Text size="sm">
                    {line.y !== undefined && `Y: ${line.y}`}
                    {line.x !== undefined && `X: ${line.x}`}
                    {line.label && ` - ${line.label}`}
                  </Text>
                </Group>
                <Group gap="xs">
                  <ActionIcon color="blue.6" variant='subtle' onClick={() => {
                    setNewReferenceLine({ ...line });
                    setEditingReferenceLineIndex( index );
                    setShowAddReferenceLine( true );
                  }}>
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon color="danger.6" variant='subtle' onClick={() => handleRemoveReferenceLine( index )}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            </Paper>
          ))}
        </Stack>
      )}

       {/* Add reference line form */}
        {showAddReferenceLine ? (
        <Paper p="sm" withBorder>
          <Stack gap="xs">
            <Group grow>
              <TextInput
                label={t( 'widget_builder_props_xvalue_label','X Value' )}
                placeholder={t( 'widget_builder_props_xvalue_placeholder','e.g. 100' )}
                type="number"
                value={newReferenceLine.x || ''}
                onChange={e => setNewReferenceLine({ ...newReferenceLine, x: e.target.value })}
              />
              <TextInput
                label={t( 'widget_builder_props_yvalue_label','Y Value' )}
                placeholder={t( 'widget_builder_props_yvalue_placeholder','e.g. 40' )}
                type="number"
                value={newReferenceLine.y?.toString() || ''}
                onChange={e => setNewReferenceLine({ ...newReferenceLine, y: parseFloat( e.target.value ) || undefined })}
              />
            </Group>
            <Group grow>
              <TextInput
                label={t( 'widget_builder_props_label_label','Label' )}
                placeholder={t( 'widget_builder_props_label_placeholder','e.g. Average sales' )}
                value={newReferenceLine.label || ''}
                onChange={e => setNewReferenceLine({ ...newReferenceLine, label: e.target.value })}
              />
              <ColorInput
                label={t( 'widget_builder_props_color_label','Color' )}
                placeholder={t( 'widget_builder_props_color_placeholder','Pick a color' )}
                value={newReferenceLine.color || ''}
                onChange={color => setNewReferenceLine({ ...newReferenceLine, color })}
                format="hexa"
                swatches={[ '#25262b', '#868e96', '#fa5252', '#e64980', '#be4bdb', '#7950f2', '#4c6ef5', '#228be6', '#15aabf', '#12b886', '#40c057', '#82c91e', '#fab005', '#fd7e14' ]}
              />
            </Group>
            <Group justify="right" mt="xs">
              <Button variant="subtle" onClick={() => {
                setShowAddReferenceLine( false );
                setEditingReferenceLineIndex( null );
                setNewReferenceLine({});
              }}>
                {t( 'widget_builder_props_cancel_button','Cancel' )}
              </Button>
              <Button onClick={handleAddReferenceLine}>
                {editingReferenceLineIndex !== null
                  ? t( 'widget_builder_props_update_button','Update' )
                  : t( 'widget_builder_props_add_button','Add' )}
              </Button>
            </Group>
          </Stack>
        </Paper>
      ) : (
        <Button leftSection={<IconPlus size={16} />} variant="light" onClick={() => setShowAddReferenceLine( true )} fullWidth>
          {t( 'widget_builder_props_add_reference_line_button','Add Reference Line' )}
        </Button>
      )}
    </Stack>
  );
};


// Critical Stock (list) props — TV1-1933 (S18), regrained by TV1-2392 (S6).
//
// The runway threshold is GONE from this panel: the list is no longer cut
// client-side by days-of-runway (that is a display column now), so an input for
// it would persist a `threshold` key nothing reads. The only widget-specific
// config left is which columns to show, and that list is DERIVED from
// `ALL_CRITICAL_STOCK_COLUMNS` so the editor cannot drift from the table again.
const CriticalStockPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetComponentProps = config.widgetComponentProps || {};

  const handleUpdate = ( key: string, value: any ) =>
    updateConfig( 'widgetComponentProps', { ...widgetComponentProps, [key]: value });

  const columns: string[] = widgetComponentProps.columns?.length ? widgetComponentProps.columns : ALL_CRITICAL_STOCK_COLUMNS;

  return (
    <Stack gap="md">
      <MultiSelect
        label={t( 'widget_critical_stock_columns_label', 'Columns' )}
        description={t( 'widget_critical_stock_columns_description', 'Columns shown in the list' )}
        data={ALL_CRITICAL_STOCK_COLUMNS.map( column => ({
          value: column,
          label: t( ...CRITICAL_STOCK_COLUMN_LABELS[column])
        }))}
        value={columns}
        onChange={value => handleUpdate( 'columns', value )}
        clearable={false}
      />
    </Stack>
  );
};

// Table / Pivot props — TV1-1929 (S14).
//
// Pivoting IS this widget's configuration: in Cube a flat table and a pivot are
// the same query with a different `pivotConfig` ({ x: rows, y: columns }), so the
// UI is just a pair of MultiSelects over the dimensions the user already picked.
// Leave both empty for an ordinary flat table (Cube's own default puts the
// dimensions on rows and the measures on columns).
//
// A plain pair of MultiSelects, not drag-and-drop: this matches the
// criticalStock props component and buys the whole feature at v1 cost.
const TablePropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetComponentProps = config.widgetComponentProps || {};
  const pivotConfig = widgetComponentProps.pivotConfig || {};

  const handleUpdate = ( key: string, value: any ) =>
    updateConfig( 'widgetComponentProps', { ...widgetComponentProps, [key]: value });

  const handlePivotUpdate = ( axis: 'x' | 'y', value: string[]) =>
    handleUpdate( 'pivotConfig', { ...pivotConfig, [axis]: value });

  const dimensionOptions = ( config.dimensions || [])
    .filter(( dimension ) => Boolean( dimension.field ))
    .map(( dimension ) => ({ value: dimension.field, label: dimension.label || dimension.field }));

  // `visibleColumns` keys off the column `dataIndex`. For a flat table that IS
  // the member name, so the user's own dimensions + measures are the option list.
  // (Under a pivot the dataIndexes are synthesized from the DATA — `Germany.male.
  // Orders.count` — so they cannot be enumerated here; leaving the selection empty
  // means "all columns", which is the right behaviour for a pivot.)
  const columnOptions = [
    ...dimensionOptions,
    ...( config.measures || []).map(( measure ) => ({ value: measure, label: measure }))
  ];

  return (
    <Stack gap="md">
      <MultiSelect
        label={t( 'widget_table_pivot_rows_label', 'Rows (pivot X axis)' )}
        description={t( 'widget_table_pivot_rows_description', 'Dimensions listed down the rows. Leave empty for a flat table.' )}
        data={dimensionOptions}
        value={pivotConfig.x || []}
        onChange={( value ) => handlePivotUpdate( 'x', value )}
        clearable
      />
      <MultiSelect
        label={t( 'widget_table_pivot_columns_label', 'Columns (pivot Y axis)' )}
        description={t( 'widget_table_pivot_columns_description', 'Dimensions spread across the columns. Set one to pivot the table.' )}
        data={dimensionOptions}
        value={pivotConfig.y || []}
        onChange={( value ) => handlePivotUpdate( 'y', value )}
        clearable
      />
      <MultiSelect
        label={t( 'widget_table_visible_columns_label', 'Visible columns' )}
        description={t( 'widget_table_visible_columns_description', 'Columns shown, in this order. Leave empty to show all.' )}
        data={columnOptions}
        value={widgetComponentProps.visibleColumns || []}
        onChange={( value ) => handleUpdate( 'visibleColumns', value )}
        clearable
      />
      <Switch
        label={t( 'widget_table_show_totals_label', 'Show totals row' )}
        checked={Boolean( widgetComponentProps.showTotals )}
        onChange={( event ) => handleUpdate( 'showTotals', event.currentTarget.checked )}
      />
    </Stack>
  );
};

// Text / Markdown props — TV1-1930 (S15).
//
// The ENTIRE configuration of a dataless widget: its content. No cube, no
// measures, no dimensions — the builder hides those sections for this type
// entirely (see `sectionsForType` in `WidgetBuilder.tsx`).
const TextPropsComponent = ({ config, updateConfig }: WidgetPropsComponentProps ) => {
  const { t } = useTranslation( 'widgets' );
  const widgetComponentProps = config.widgetComponentProps || {};

  return (
    <Stack gap="md">
      <Textarea
        label={t( 'widget_text_content_label', 'Content (Markdown)' )}
        description={t(
          'widget_text_content_description',
          'Supports Markdown, including GitHub-flavoured tables. Raw HTML is not rendered.'
        )}
        placeholder={t( 'widget_text_content_placeholder', '## Summary\n\nSales rose 12% this month...' )}
        value={widgetComponentProps.content ?? ''}
        onChange={( event ) =>
          updateConfig( 'widgetComponentProps', { ...widgetComponentProps, content: event.currentTarget.value })
        }
        autosize
        minRows={8}
        maxRows={20}
        radius="xs"
      />
    </Stack>
  );
};

// Map of widget types to their props components
export const widgetPropsComponents: Partial<Record<WidgetType, React.ComponentType<WidgetPropsComponentProps>>> = {
  areaChart: AreaChartPropsComponent,
  barChart: BarChartPropsComponent,
  lineChart: LineChartPropsComponent,
  pieChart: PieChartPropsComponent,
  donutChart: DonutChartPropsComponent,
  scatterChart: ScatterChartPropsComponent,
  criticalStock: CriticalStockPropsComponent,
  table: TablePropsComponent,
  // TV1-1930 (S15) — shared verbatim with the MOBILE builder (see
  // `MobileWidgetProps`); the KPI renders identically on both devices.
  kpi: KpiPropsComponent,
  text: TextPropsComponent
};

// Function to get the appropriate props component based on widget type
export const getWebWidgetPropsComponent = ( widgetType: WidgetType ): React.ComponentType<WidgetPropsComponentProps> => {
  const component = widgetPropsComponents[widgetType];
  return component || DefaultWidgetPropsComponent;
};